import clsx from "clsx";
import { sortBy } from "lodash";
import React from "react";
import { Experience, UnitType } from "types";
import shallow from "zustand/shallow";
import { AttendeeLineItem, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { getCatalogItemsByUnitType } from "@/models/Catalog";
import { getMaxAttendees } from "@/models/Experience";

export const CreatePurchaseAttendeesPicker = React.memo(() => {
    const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const equipmentLineItems = useCreatePurchaseStore((state) => state.getEquipmentLineItems());

    const totalAttendees = useCreatePurchaseStore((state) =>
        state.getAttendeeLineItems().reduce((sum, li) => sum + li.quantity, 0),
    );

    const secondaryItems = getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.DEMOGRAPHIC_SECONDARY);
    const sortedItems = sortBy(secondaryItems, "sequence");

    if (sortedItems.length === 0) {
        return null;
    }

    const maxAttendees = getMaxAttendees(experience, equipmentLineItems);
    const error =
        totalAttendees < 1
            ? "Quantity must be at least 1"
            : maxAttendees > 0 && totalAttendees > maxAttendees
            ? `Attendees cannot exceed ${maxAttendees}`
            : undefined;

    return (
        <div className="space-y-2">
            <div className={clsx("text-sm font-bold", { "text-danger": error })}>{error ?? "Attendees"}</div>
            <div className="flex flex-col space-y-2">
                {sortedItems.map((catalogItem) => (
                    <AttendeeItem key={catalogItem.id} catalogItem={catalogItem} />
                ))}
            </div>
        </div>
    );
});

interface AttendeeItemProps {
    catalogItem: any;
}

const AttendeeItem = ({ catalogItem }: AttendeeItemProps) => {
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);

    const { quantity } = useCreatePurchaseStore((state) => {
        const match = state
            .getAttendeeLineItems()
            .find((lineItem: AttendeeLineItem) => lineItem.template?.id === catalogItem.template?.id);
        return { quantity: match?.quantity ?? 0 };
    }, shallow);

    const handleChange = (newQuantity: number) => {
        useCreatePurchaseStore
            .getState()
            .actions.updateAttendeeQuantity(
                activeItemIndex,
                catalogItem.template?.id,
                newQuantity,
                catalogItem.name,
                catalogItem.sequence,
            );
    };

    return (
        <div className="flex items-center justify-start" data-testid="create-purchase-attendees-picker-item">
            <QuantitySpinner
                shouldSyncValue
                lockAtMax={false}
                value={quantity}
                min={0}
                size="large"
                classNames={{ fieldset: "mr-3", label: "font-bold" }}
                onChange={handleChange}
            />
            <div className="min-w-0 flex-1">
                <NoTranslate as="div" className="break-all font-bold">
                    {catalogItem.name}
                </NoTranslate>
                {catalogItem.caption && <div className="text-gray-darker line-clamp-2">{catalogItem.caption}</div>}
            </div>
        </div>
    );
};
